<template>
  <div class="modal fade" id="embed-builder-modal" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('embed_builder.modal.embed_map') }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('shared.close')"></button>
        </div>
        <div class="modal-body p-5">
          <div class="row">
            <!-- Options Column -->
            <div class="col-md-5">
              <h6 class="mb-3">{{ $t('embed_builder.modal.display_options') }}</h6>

              <div class="form-check form-switch mb-3" v-if="supportsHighlights">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="showHighlights"
                  v-model="options.showHighlights"
                >
                <label class="form-check-label" for="showHighlights">
                  {{ $t('embed_builder.modal.show_highlights_pois') }}
                </label>
              </div>

              <div class="form-check form-switch mb-3">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="showElevation"
                  v-model="options.showElevation"
                >
                <label class="form-check-label" for="showElevation">
                  {{ $t('embed_builder.modal.show_elevation_profile') }}
                </label>
              </div>

              <div class="form-check form-switch mb-3 ms-4" v-show="options.showElevation">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="elevationMinimized"
                  v-model="options.elevationMinimized"
                >
                <label class="form-check-label" for="elevationMinimized">
                  {{ $t('embed_builder.modal.start_minimized') }}
                </label>
              </div>

              <template v-if="supportsRoutePanels">
                <div class="form-check form-switch mb-3">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="showRoutesPanel"
                    v-model="options.showRoutesPanel"
                  >
                  <label class="form-check-label" for="showRoutesPanel">
                    {{ $t('embed_builder.modal.show_routes_panel') }}
                  </label>
                </div>

                <div class="form-check form-switch mb-3 ms-4" v-show="options.showRoutesPanel">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="routesPanelCollapsed"
                    v-model="options.routesPanelCollapsed"
                  >
                  <label class="form-check-label" for="routesPanelCollapsed">
                    {{ $t('embed_builder.modal.start_collapsed') }}
                  </label>
                </div>
              </template>

              <div class="form-check form-switch mb-3">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="endpointLabels"
                  v-model="options.endpointLabels"
                >
                <label class="form-check-label" for="endpointLabels">
                  {{ $t('embed_builder.modal.label_start_end_a_b') }}
                </label>
              </div>

              <div class="form-check form-switch mb-3 ms-4" v-show="options.endpointLabels">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="endpointLabelCountry"
                  v-model="options.endpointLabelCountry"
                >
                <label class="form-check-label" for="endpointLabelCountry">
                  {{ $t('embed_builder.modal.include_country') }}
                </label>
              </div>

              <div class="form-check form-switch mb-3">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="scrollZoom"
                  v-model="options.scrollZoom"
                >
                <label class="form-check-label" for="scrollZoom">
                  {{ $t('embed_builder.modal.zoom_with_mouse_wheel') }}
                </label>
                <div class="form-text small">{{ $t('embed_builder.modal.zoom_with_mouse_wheel_hint') }}</div>
              </div>

              <hr class="my-4">

              <h6 class="mb-3">{{ $t('embed_builder.modal.layout') }}</h6>
              <div class="btn-group w-100 mb-2" role="group">
                <input type="radio" class="btn-check" id="layoutOverlay" value="overlay" v-model="options.layout">
                <label class="btn btn-outline-light btn-sm" for="layoutOverlay">{{ $t('embed_builder.modal.layout_overlay') }}</label>
                <input type="radio" class="btn-check" id="layoutStacked" value="stacked" v-model="options.layout">
                <label class="btn btn-outline-light btn-sm" for="layoutStacked">{{ $t('embed_builder.modal.layout_stacked') }}</label>
              </div>
              <div class="form-text small mb-3">{{ $t('embed_builder.modal.layout_stacked_hint') }}</div>

              <hr class="my-4">

              <h6 class="mb-3">{{ $t('embed_builder.modal.units') }}</h6>
              <div class="btn-group w-100 mb-3" role="group">
                <input type="radio" class="btn-check" id="unitsMetric" value="metric" v-model="options.units">
                <label class="btn btn-outline-light btn-sm" for="unitsMetric">{{ $t('embed_builder.modal.km_m') }}</label>
                <input type="radio" class="btn-check" id="unitsImperial" value="imperial" v-model="options.units">
                <label class="btn btn-outline-light btn-sm" for="unitsImperial">{{ $t('embed_builder.modal.miles_ft') }}</label>
              </div>

              <hr class="my-4">

              <h6 class="mb-3">{{ $t('embed_builder.modal.embed_size') }}</h6>
              <div class="row g-2 mb-3">
                <div class="col">
                  <label class="form-label small text-muted">{{ $t('embed_builder.modal.width') }}</label>
                  <input
                    type="text"
                    class="form-control form-control-sm rounded-pill py-2 px-3"
                    v-model="dimensions.width"
                    placeholder="100%"
                  >
                </div>
                <div class="col">
                  <label class="form-label small text-muted">{{ $t('embed_builder.modal.height') }}</label>
                  <input
                    type="text"
                    class="form-control form-control-sm rounded-pill py-2 px-3"
                    v-model="dimensions.height"
                    placeholder="500"
                  >
                </div>
              </div>
            </div>

            <!-- Preview Column -->
            <div class="col-md-7">
              <h6 class="mb-3">{{ $t('shared.preview') }}</h6>
              <div class="embed-preview-container">
                <div v-if="iframeLoading" class="iframe-loading">
                  <div class="spinner"></div>
                  <span>{{ $t('embed_builder.modal.loading_preview') }}</span>
                </div>
                <iframe
                  :src="embedUrl"
                  :width="previewWidth"
                  :height="previewHeight"
                  frameborder="0"
                  allowfullscreen
                  class="embed-preview"
                  :class="{ 'iframe-hidden': iframeLoading }"
                  @load="onIframeLoad"
                ></iframe>
              </div>
            </div>
          </div>

          <!-- Embed Code -->
          <div class="card p-3 mt-3">
            <div class="d-flex justify-content-between">
              <div>
                <h6 class="mb-2">{{ $t('embed_builder.modal.embed_code') }}</h6>
                <p class="text-muted small mb-2">{{ $t('embed_builder.modal.copy_and_paste_this_code_into') }}</p>
              </div>
              <button class="btn btn-small btn-seconday" type="button" @click="copyCode">
                <span class="material-icons" style="font-size: 18px;">content_copy</span>
                {{ copied ? $t('common.copied') : $t('common.copy') }}
              </button>
            </div>
            <textarea
              class="form-control embed-code-textarea"
              :value="embedCode"
              readonly
              rows="3"
              ref="codeTextarea"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('shared.close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import copyToClipboard from '../../utils/helpers/CopyToClipboardHelper';
import {
  EmbedLayout,
  EmbedMode,
  embedIframeCode,
  embedUrl,
  embedWidthAttribute,
  embedHeightAttribute,
  previewHeight,
  supportsHighlights,
  supportsRoutePanels,
} from '../../utils/embeds/embedUrlParams';

declare global {
  interface Window {
    embedBuilderConfig?: {
      hikeId?: string;
      recordingId?: string;
      embedBaseUrl: string;
      isMultiDay?: boolean;
      // Absent on the hike pages that predate recordings embeds — they mean 'hike'.
      mode?: EmbedMode;
    };
  }
}

export default defineComponent({
  name: 'EmbedBuilderModal',
  data() {
    return {
      options: {
        showHighlights: true,
        showElevation: true,
        elevationMinimized: true,
        showRoutesPanel: true,
        routesPanelCollapsed: true,
        units: 'metric',
        layout: 'overlay' as EmbedLayout,
        scrollZoom: true,
        endpointLabels: false,
        endpointLabelCountry: false
      },
      dimensions: {
        width: '100%',
        height: '400'
      },
      copied: false,
      iframeLoading: true
    };
  },
  computed: {
    mode(): EmbedMode {
      return window.embedBuilderConfig?.mode || 'hike';
    },

    /** A recording has no POI highlights and no routes panel to configure. */
    supportsHighlights(): boolean {
      return supportsHighlights(this.mode);
    },

    supportsRoutePanels(): boolean {
      return supportsRoutePanels(this.mode);
    },

    embedBaseUrl(): string {
      return window.embedBuilderConfig?.embedBaseUrl || '';
    },

    embedUrl(): string {
      return embedUrl(this.embedBaseUrl, this.options, this.mode);
    },

    previewWidth(): string {
      // For preview, use 100% of container
      return '100%';
    },

    previewHeight(): string {
      return previewHeight(this.dimensions.height);
    },

    embedWidth(): string {
      return embedWidthAttribute(this.dimensions.width);
    },

    embedHeight(): string {
      return embedHeightAttribute(this.dimensions.height);
    },

    embedCode(): string {
      return embedIframeCode(this.embedUrl, this.dimensions.width, this.dimensions.height);
    }
  },
  watch: {
    embedUrl() {
      // Show loading when URL changes (options toggled)
      this.iframeLoading = true;
    }
  },
  methods: {
    onIframeLoad() {
      this.iframeLoading = false;
    },
    copyCode() {
      copyToClipboard(this.embedCode, (success, error) => {
        if (error) {
          alert(this.$t('embed_builder.modal.failed_to_copy_code'));
          return;
        }
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      });
    }
  }
});
</script>

<style scoped>
.embed-preview-container {
  background: #2a2d35;
  border-radius: 8px;
  padding: 8px;
  min-height: 200px;
  position: relative;
}

.embed-preview {
  border-radius: 6px;
  width: 100%;
  background: #1a1b21;
  transition: opacity 0.2s ease;
}

.embed-preview.iframe-hidden {
  opacity: 0;
}

.iframe-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  background: #1a1b21;
  border-radius: 6px;
  margin: 8px;
}

.iframe-loading .spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 223, 70, 0.2);
  border-top-color: #FFDF46;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.embed-code-section {
  border-radius: 8px;
  padding: 16px;
}

.embed-code-section h6 {
  color: #fff;
}

.embed-code-section p {
  color: rgba(255, 255, 255, 0.6);
}

.embed-code-textarea {
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 11px;
  background: #1a1b21 !important;
  color: #e0e0e0 !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  border-radius: 6px !important;
  resize: none;
  padding: 12px;
  margin-bottom: 12px;
}

.embed-code-textarea:focus {
  background: #1a1b21 !important;
  color: #e0e0e0 !important;
  border-color: #FFDF46 !important;
  box-shadow: none !important;
}

.btn-copy {
  width: 100%;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  padding: 12px 16px;
}

/* Switch toggle styling */
.form-check-input {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
}

.form-check-input:checked {
  background-color: #FFDF46;
  border-color: #FFDF46;
}

/* Ensure the circle/knob has high contrast */
.form-check-input[type="checkbox"] {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23000'/%3e%3c/svg%3e");
}

.form-check-input[type="checkbox"]:checked {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23000'/%3e%3c/svg%3e");
}

.form-check-input:focus {
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: none;
  outline: none;
}

.form-check-input:checked:focus {
  border-color: #FFDF46;
  box-shadow: none;
  outline: none;
}

.btn-primary {
  background-color: #FFDF46;
  border-color: #FFDF46;
  color: #000;
  font-weight: 500;
}

.btn-primary:hover {
  background-color: #e6c93f;
  border-color: #e6c93f;
  color: #000;
}

.modal-content {
  border-radius: 12px;
}

.modal-header {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.modal-footer {
  border-top-color: rgba(255, 255, 255, 0.1);
}
</style>
